import React, { useEffect, useState } from "react";
import { ASSET_PATHS } from "@satchel/core";
import { siteMetadata } from "../../siteMetadata";
import { isElectron } from "../../utils/runtime";

export interface NavItem<View extends string> {
  view: View;
  label: string;
  icon: React.ReactNode;
}

export interface NavGroup<View extends string> {
  /** Heading shown above the group; none for the main group. */
  label?: string;
  items: NavItem<View>[];
}

export interface AppShellProps<View extends string> {
  groups: NavGroup<View>[];
  currentView: View;
  onNavigate: (view: View) => void;
  /** Shown in the navigation just above its last group. */
  status?: React.ReactNode;
  children: React.ReactNode;
}

// macOS hides the desktop app's title bar and draws its window buttons over the
// top-left corner, so that corner is left clear and kept draggable.
const macDesktop = isElectron && /Mac/.test(navigator.userAgent);
const dragRegion = { WebkitAppRegion: "drag" } as React.CSSProperties;
const noDragRegion = { WebkitAppRegion: "no-drag" } as React.CSSProperties;

const Brand: React.FC = () => (
  <div className="flex items-center gap-2.5">
    <img src={ASSET_PATHS.SATCHEL_LOGO} alt="" className="h-8 w-8" />
    <span className="text-base font-semibold tracking-wide text-sa-text">{siteMetadata.title}</span>
  </div>
);

function NavGroupList<View extends string>({
  group,
  currentView,
  onNavigate,
}: {
  group: NavGroup<View>;
  currentView: View;
  onNavigate: (view: View) => void;
}) {
  return (
    <div>
      {group.label && (
        <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-sa-text-dim">
          {group.label}
        </p>
      )}
      <ul className="space-y-1">
        {group.items.map((item) => {
          const active = item.view === currentView;
          return (
            <li key={item.view}>
              <button
                type="button"
                onClick={() => onNavigate(item.view)}
                aria-current={active ? "page" : undefined}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-sa-text-muted hover:bg-sa-surface-hover hover:text-sa-text"
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The groups top down, except the last, which sits at the bottom under `status`. */
function Navigation<View extends string>({
  groups,
  currentView,
  onNavigate,
  status,
}: Omit<AppShellProps<View>, "children">) {
  const last = groups[groups.length - 1];
  const list = (group: NavGroup<View>, key: React.Key) => (
    <NavGroupList key={key} group={group} currentView={currentView} onNavigate={onNavigate} />
  );
  return (
    <nav aria-label="Main" className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4">
      {groups.slice(0, -1).map((group, index) => list(group, group.label ?? index))}
      {last && (
        <div className="mt-auto space-y-5">
          {status}
          {list(last, "last")}
        </div>
      )}
    </nav>
  );
}

const VersionLabel: React.FC = () => (
  <p className="border-t border-sa-border px-6 py-3 text-xs text-sa-text-dim">
    {siteMetadata.versionLabel}
  </p>
);

/**
 * The signed-in app's frame: a fixed sidebar on wide screens, a top bar with a
 * navigation drawer on narrow ones.
 */
export function AppShell<View extends string>({
  groups,
  currentView,
  onNavigate,
  status,
  children,
}: AppShellProps<View>) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!drawerOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [drawerOpen]);

  const navigate = (view: View) => {
    setDrawerOpen(false);
    onNavigate(view);
  };

  return (
    <div className="min-h-screen bg-sa-bg text-sa-text">
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col border-r border-sa-border bg-sa-surface md:flex">
        <div className={`px-6 pb-4 ${macDesktop ? "pt-11" : "pt-5"}`} style={dragRegion}>
          <Brand />
        </div>
        <div className="flex min-h-0 flex-1 flex-col" style={noDragRegion}>
          <Navigation
            groups={groups}
            currentView={currentView}
            onNavigate={navigate}
            status={status}
          />
          <VersionLabel />
        </div>
      </aside>

      <header
        className={`sticky top-0 z-30 flex items-center justify-between border-b border-sa-border bg-sa-bg/90 px-4 backdrop-blur md:hidden ${
          macDesktop ? "h-14 pl-20" : "h-14"
        }`}
        style={dragRegion}
      >
        <Brand />
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          className="rounded-lg p-2 text-sa-text-muted hover:bg-sa-surface-hover hover:text-sa-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          style={noDragRegion}
        >
          <svg
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      </header>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 h-full w-full bg-black/50"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85%] flex-col bg-sa-surface shadow-xl">
            <div className="flex h-14 items-center px-6">
              <Brand />
            </div>
            <Navigation
              groups={groups}
              currentView={currentView}
              onNavigate={navigate}
              status={status}
            />
            <VersionLabel />
          </div>
        </div>
      )}

      {macDesktop && (
        <div className="fixed inset-x-0 top-0 hidden h-8 md:block" style={dragRegion} />
      )}

      <main className="md:pl-60">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</div>
      </main>
    </div>
  );
}
